// The upload re-auth recovery state (F2 / #808) — a leaf module so BOTH the
// upload-path recovery (connector-upload-auth.ts) and the session-refresh
// primitive (session-refresh.ts) can touch it without a circular import.
//
// Holds TWO pieces of state, and `resumeUploadsAfterReauth()` is the SINGLE
// convergent reset for BOTH:
//   - the PAUSE LATCH — set when an expired-session 401 could not be silently
//     refreshed; uploadData early-returns while it is TRUE so the SDK's ~5 s
//     retry is an O(1) no-op (queue preserved, un-acked).
//   - the INDETERMINATE-REFRESH COUNTER — the bounded run of consecutive
//     /auth/refresh transient failures; recoverUploadAuth escalates to a pause
//     once it crosses the budget (so a persistently-down refresh on an expired
//     session cannot wedge forever).
//
// Why a leaf: connector-upload-auth.ts imports `refreshSession` from
// session-refresh.ts; the convergent-resume fix requires `refreshSession` to
// resume on every /auth/refresh 2xx (so a keep-alive slide clears a pause too).
// If this state lived in connector-upload-auth.ts, session-refresh.ts importing
// it back would be a cycle. Keeping it in a dependency-free leaf both import
// removes the cycle.
//
// CRITICAL INVARIANT (audit-2): resume clears BOTH. The resume fires through the
// slide / fetchCredentials path (refreshSession → resumeUploadsAfterReauth),
// which does NOT go through recoverUploadAuth — so if resume only cleared the
// latch, the counter would stay at its escalation value and the NEXT unrelated
// 401 blip would mis-escalate to a pause on its first try. Resume zeros both.

let uploadsPausedForReauth = false
let indeterminateRefreshRun = 0

/** Whether uploads are currently paused awaiting re-auth. */
export function areUploadsPausedForReauth(): boolean {
  return uploadsPausedForReauth
}

/** Pause uploads — an expired session that could not be silently refreshed. */
export function pauseUploadsForReauth(): void {
  uploadsPausedForReauth = true
}

/**
 * Resume uploads — a fresh credential proved the session is valid again. Clears
 * ALL re-auth recovery state (the latch AND the indeterminate-refresh run), so
 * a later unrelated 401 episode starts from a clean budget. See the CRITICAL
 * INVARIANT above.
 */
export function resumeUploadsAfterReauth(): void {
  uploadsPausedForReauth = false
  indeterminateRefreshRun = 0
}

/** Increment and return the consecutive indeterminate-refresh count. */
export function bumpIndeterminateRefreshRun(): number {
  return ++indeterminateRefreshRun
}

/** Reset the indeterminate-refresh run on a DEFINITIVE refresh outcome (the
 *  /auth/refresh returned, valid or expired — the indeterminate run is over). */
export function resetIndeterminateRefreshRun(): void {
  indeterminateRefreshRun = 0
}

/** Test seam — reset all re-auth recovery state (latch + counter) between scenarios. */
export function _resetUploadAuthLatch(): void {
  uploadsPausedForReauth = false
  indeterminateRefreshRun = 0
}
